import React, { useState, useEffect, useRef, useCallback } from "react";

import { Grid, Typography, Button, Paper, TextField } from "@mui/material";

import { GenerateInteger } from "../helpers/RandomGenerator";

import { useTranslation } from "react-i18next";
import AutohelperLogsTable from "../components/Tables/AutohelperLogsTable";
import AdvancedTablePagination from "../components/AdvancedTablePagination";
import { useBackend } from "../context/BackendContext";
import LoadingOverlay from "react-loading-overlay-ts";
import { useInterval } from "../useInterval";
import { ResponseCode } from "../enums/ResponseCode";

export default function AutohelperLogs() {
  const backend = useBackend();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchLoginInput, setSearchLoginInput] = useState("");
  const [searchParams, setSearchParams] = useState({
    searchLogin: "",
    pageNumber: 0,
    pageSize: 10,
  });
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(100);
  const lastRequestId = useRef(0);
  const updatesLockCounter = useRef(0);

  const refreshTable = useCallback(
    (searchLogin, pageNumber, pageSize, noLoading) => {
      if (!noLoading) {
        setIsLoading(true);
      }
      ++updatesLockCounter.current;
      var id = GenerateInteger();
      lastRequestId.current = id;
      backend
        .bckAutohelperLogs(searchLogin, pageNumber + 1, pageSize)
        .then((json) => {
          if (!noLoading) {
            setIsLoading(false);
          }
          --updatesLockCounter.current;
          if (lastRequestId.current === id) {
            if (json.code === 0) {
              setTotalRows(json.content.count);
              setTableData((prev) => {
                if (updatesLockCounter.current === 0) {
                  return json.content.list;
                } else {
                  return prev;
                }
              });
            } else if (json.code === ResponseCode.ACCESS_DENIED) {
              setError(t("AUTH_ERROR"));
            } else {
              setError(t("REQUEST_ERROR"));
            }
          }
        });
    },
    [t, backend]
  );

  useEffect(() => {
    return () => {
      lastRequestId.current = null;
    };
  }, []);

  useEffect(() => {
    refreshTable(
      searchParams.searchText,
      searchParams.pageNumber,
      searchParams.pageSize,
      false
    );
  }, [searchParams]);

  const handleChangePage = (event, newPage) => {
    setSearchParams({ ...searchParams, pageNumber: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    var newRowsPerPage = parseInt(event.target.value, 10);
    setSearchParams({
      ...searchParams,
      pageNumber: 0,
      pageSize: newRowsPerPage,
    });
  };

  const handleSearchPress = () => {
    setSearchParams({
      ...searchParams,
      searchText: searchLoginInput,
      pageNumber: 0,
    });
  };

  useInterval(() => {
    if (updatesLockCounter.current === 0) {
      refreshTable(
        searchParams.searchText,
        searchParams.pageNumber,
        searchParams.pageSize,
        true
      );
    }
  }, 2000);

  return (
    <LoadingOverlay active={isLoading} spinner>
      <Paper style={{ overflow: "auto", padding: "24px", maxHeight: "85vh" }}>
        <Grid container direction="column" spacing={2}>
          <Typography variant="h5" color="textSecondary">
            {t("Autohelper logs table")}
          </Typography>
          <Typography color="secondary">{error}</Typography>
          <Grid item container direction="row">
            <Grid item>
              <TextField
                label={t("Login")}
                value={searchLoginInput}
                onChange={(e) => setSearchLoginInput(e.target.value)}
                style={{ marginRight: "12px" }}
              />
            </Grid>
          </Grid>
          <Grid item container spacing={2}>
            <Grid item>
              <Button
                onClick={handleSearchPress}
                variant="contained"
                color="primary"
              >
                {t("Search")}
              </Button>
            </Grid>
          </Grid>
          <div style={{ maxHeight: "70vh", overflow: "auto" }}>
            <AdvancedTablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              totalRows={totalRows}
              pageSize={searchParams.pageSize}
              pageNumber={searchParams.pageNumber}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            >
              <AutohelperLogsTable data={tableData} />
            </AdvancedTablePagination>
          </div>
        </Grid>
      </Paper>
    </LoadingOverlay>
  );
}
